import { alpha, Box, useTheme } from '@mui/material'
import { ParentSize } from '@visx/responsive'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { red } from '../../assets/colors'
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale'
import { SegmentInfo } from '../../pages/participantOpenInterest'
import { localPoint } from '@visx/event'
import { formatLargeNumber } from '../../util/screener'
import { BarLabelView } from '../../components/participantOpenInterest/BarLabel'
import { computeOiChartDataSignature, oiChartDataEqual } from './oiChartData'
import OIChartBarsLayer from './OIChartBarsLayer'
import OIChartTooltipHost, { OIChartTooltipHandle } from './OIChartTooltipHost'

const DEFAULT_CHART_MARGIN = { top: 0, right: 0, bottom: 15, left: 0 } as const
const WIDGET_CHART_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 } as const
export const OI_WIDGET_RIGHT_SCALE_WIDTH = 44
export const OI_WIDGET_BOTTOM_SCALE_WIDTH = 20

export type ParticipantChartMetaInfo = {
    colors: Map<string, string>,
    specificColors?: Record<string, any>,
    rightAxisLabel?: string,
    leftAxisLabel?: string
}

export type OiChartScaleSide = 'left' | 'right'

export type ParticipantChartProps = {
    margin?: { top: number; right: number; bottom: number; left: number },
    segmentInfo: SegmentInfo,
    showOI?: boolean,
    replayKey?: number
    /** Higher band padding for narrow sidebar previews — thinner strike bars. */
    compactBars?: boolean
    /** Widget mode puts the OI axis on the right so it matches other dashboard charts. */
    scaleSide?: OiChartScaleSide
    showLegend?: boolean
    showTooltip?: boolean
    onHoverRow?: (row: { date: string } & Record<string, number | string> | null) => void
}

export const participantBarDefaultColor = red

type OIChartCanvasProps = ParticipantChartProps & {
    width: number
    height: number
}

const OIChartCanvas = ({
    margin = DEFAULT_CHART_MARGIN,
    segmentInfo,
    showOI = false,
    replayKey = 0,
    compactBars = false,
    scaleSide = 'left',
    showTooltip = true,
    onHoverRow,
    width,
    height,
}: OIChartCanvasProps) => {
    const { data, metaInfo } = segmentInfo
    const minNum: number = -1e3
    const theme = useTheme()
    const crosshairRef = useRef<SVGLineElement>(null)
    const tooltipRef = useRef<OIChartTooltipHandle>(null)
    const lastHoverStrikeRef = useRef<string | null>(null)
    const hoverRafRef = useRef<number | null>(null)
    const pendingHoverEventRef = useRef<React.MouseEvent<SVGRectElement> | null>(null)
    const barWidthRef = useRef(0)
    const [animatedData, setAnimatedData] = useState(data)
    const previousDataRef = useRef<any[]>([])
    const prevReplayKeyRef = useRef(replayKey)

    const chartDataSignature = useMemo(() => computeOiChartDataSignature(data), [data])
    
    // Function to check if data has meaningfully changed (not just object reference)
    const hasDataChanged = useCallback((newData: any[], oldData: any[]) => {
        if (newData.length !== oldData.length) return true
        
        for (let i = 0; i < newData.length; i++) {
            const newItem = newData[i]
            const oldItem = oldData[i]
            
            if (!oldItem || newItem.date !== oldItem.date) return true
            
            // Check all numeric values
            for (const key in newItem) {
                if (key !== 'date' && typeof newItem[key] === 'number') {
                    if (Math.abs((newItem[key] || 0) - (oldItem[key] || 0)) > 0.01) {
                        return true
                    }
                }
            }
        }
        return false
    }, [])
    
    const onHoverRowRef = useRef(onHoverRow)
    onHoverRowRef.current = onHoverRow
    const showTooltipRef = useRef(showTooltip)
    showTooltipRef.current = showTooltip
    const isWidgetLayout = scaleSide === 'right'
    const chartMargin = isWidgetLayout ? WIDGET_CHART_MARGIN : margin
    const leftScaleWidth = isWidgetLayout ? 0 : 60
    const rightScaleWidth = isWidgetLayout ? OI_WIDGET_RIGHT_SCALE_WIDTH : 0
    const bottomScaleWidth = isWidgetLayout ? OI_WIDGET_BOTTOM_SCALE_WIDTH : 30
    const xMax = (width - chartMargin.left - chartMargin.right - rightScaleWidth - leftScaleWidth)
    const yMax = height - chartMargin.top - chartMargin.bottom - bottomScaleWidth
    const keys = useMemo(() => {
        const allKeys = new Set<string>()
        data.forEach(entry => {
            Object.keys(entry).forEach(key => {
                if (key !== 'date') {
                    if (key.endsWith(' From')) {
                        allKeys.add(key.replace(' From', ''))
                    } else if (key.endsWith(' To')) {
                        allKeys.add(key.replace(' To', ''))
                    } else {
                        allKeys.add(key)
                    }
                }
            })
        })
        return Array.from(allKeys)
    }, [data])

    useEffect(() => {
        const replayRequested = prevReplayKeyRef.current !== replayKey
        prevReplayKeyRef.current = replayKey

        // Restart animation when data changes, or when user clicks Replay (same data, new replayKey)
        if (!hasDataChanged(data, previousDataRef.current) && !replayRequested) {
            return
        }
        
        // Store current data as previous for next comparison
        previousDataRef.current = JSON.parse(JSON.stringify(data))
        
        if (!showOI) {
            setAnimatedData(data)
            return
        }

        const initialDelay = 0
        const decreaseDuration = 500
        const increaseDuration = 500
        let timeoutId: ReturnType<typeof setTimeout> | undefined
        let rafId: number | null = null

        const initialData = data.map((item) => {
            const interpolated: any = { date: item.date }
            keys.forEach(key => {
                const fromKey = `${key} From`
                const toKey = `${key} To`
                if (item[fromKey] !== undefined && item[toKey] !== undefined) {
                    interpolated[key] = Number(item[fromKey] || 0)
                    interpolated[fromKey] = Number(item[fromKey] || 0)
                    interpolated[toKey] = Number(item[toKey] || 0)
                } else {
                    interpolated[key] = item[key]
                }
            })
            return interpolated
        })
        
        setAnimatedData(initialData)

        timeoutId = setTimeout(() => {
            const startTime = Date.now()

            const animate = () => {
                const elapsed = Date.now() - startTime
                const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

                let currentDecreaseProgress = 0
                let currentIncreaseProgress = 0
                
                if (elapsed < decreaseDuration) {
                    const progress = Math.min(elapsed / decreaseDuration, 1)
                    currentDecreaseProgress = easeOutCubic(progress)
                    currentIncreaseProgress = 0
                } else if (elapsed < decreaseDuration + increaseDuration) {
                    const progress = Math.min((elapsed - decreaseDuration) / increaseDuration, 1)
                    currentDecreaseProgress = 1
                    currentIncreaseProgress = easeOutCubic(progress)
                } else {
                    currentDecreaseProgress = 1
                    currentIncreaseProgress = 1
                }

                const interpolatedData = data.map((item) => {
                    const interpolated: any = { date: item.date }
                    
                    keys.forEach(key => {
                        const fromKey = `${key} From`
                        const toKey = `${key} To`
                        
                        if (item[fromKey] !== undefined && item[toKey] !== undefined) {
                            const fromValue = Number(item[fromKey] || 0)
                            const toValue = Number(item[toKey] || 0)
                            const change = toValue - fromValue
                            
                            if (change < 0) {
                                interpolated[key] = fromValue + change * currentDecreaseProgress
                            } else if (change > 0) {
                                interpolated[key] = fromValue + change * currentIncreaseProgress
                            } else {
                                interpolated[key] = fromValue
                            }
                            interpolated[fromKey] = fromValue
                            interpolated[toKey] = toValue
                        } else {
                            interpolated[key] = item[key]
                        }
                    })
                    
                    return interpolated
                })

                setAnimatedData(interpolatedData)

                if (elapsed < decreaseDuration + increaseDuration) {
                    rafId = requestAnimationFrame(animate)
                } else {
                    rafId = null
                }
            }

            animate()
        }, initialDelay)

        return () => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId)
            }
            if (rafId !== null) {
                cancelAnimationFrame(rafId)
            }
        }
        // Intentionally omit `data`/`keys` refs: chartDataSignature captures content so parent re-memos
        // (same values) do not cancel rAF mid-run (which often clipped the increase phase after 500ms).
        // eslint-disable-next-line react-hooks/exhaustive-deps -- data/keys align with chartDataSignature
    }, [chartDataSignature, showOI, replayKey])

    const colors = useMemo(() => keys.map((key) => metaInfo.colors.get(key) ?? participantBarDefaultColor), [keys, metaInfo])

    const yScaleMax = useMemo(() => {
        let maxValue = 0
        if (showOI) {
            maxValue = Math.max(0, Math.max(...(data ? data?.map((d) => {
                return Math.max(...keys?.map((key) => {
                    const fromKey = `${key} From`
                    const toKey = `${key} To`
                    const fromValue = Number(d[fromKey] !== undefined ? d[fromKey] : 0)
                    const toValue = Number(d[toKey] !== undefined ? d[toKey] : d[key] || 0)
                    return Math.max(fromValue, toValue)
                }))
            }) : [0])))
        } else {
            maxValue = Math.max(0, Math.max(...(animatedData ? animatedData?.map((d) => Math.max(...keys?.map((key) => Number(d[key])))) : [0])))
        }
        return maxValue * 1.1
    }, [data, animatedData, keys, showOI])
    
    const yScaleMin = useMemo(() => {
        let minValue = 0
        if (showOI) {
            minValue = Math.min(0, Math.min(...(data ? data?.map((d) => {
                return Math.min(...keys?.map((key) => {
                    const fromKey = `${key} From`
                    const toKey = `${key} To`
                    const fromValue = Number(d[fromKey] !== undefined ? d[fromKey] : 0)
                    const toValue = Number(d[toKey] !== undefined ? d[toKey] : d[key] || 0)
                    return Math.min(fromValue, toValue)
                }))
            }) : [0])))
        } else {
            minValue = Math.min(0, Math.min(...(animatedData ? animatedData?.map((d) => Math.min(...keys?.map((key) => Number(d[key])))) : [0])))
        }
        return minValue < 0 ? minValue * 1.1 : minValue
    }, [data, animatedData, keys, showOI])

    //scales
    const dateScale = useMemo(() => scaleBand<string>({
        domain: animatedData.map((dt) => dt.date) ?? [],
        padding: compactBars ? 0.45 : 0.2
    }), [animatedData, compactBars])
    const x1Scale = useMemo(() => {
        return scaleBand<string>({
            domain: keys,
            padding: 0
        })
    }, [keys])
    const yScale = useCallback(scaleLinear<number>({
        domain: [Math.min(0, yScaleMin), yScaleMax]
    }), [yScaleMax, yScaleMin])
    const yLeftScale = useCallback(scaleLinear<number>({
        domain: [yScaleMin, yScaleMax]
    }), [yScaleMin, yScaleMax])

    const colorScale = useCallback(scaleOrdinal<string, string>({
        domain: keys,
        range: colors
    }), [colors, keys])
    dateScale.rangeRound([0, xMax])
    x1Scale.rangeRound([0, dateScale.bandwidth()])
    yScale.rangeRound([yMax, 0])
    yLeftScale.rangeRound([yMax, 0])

    const maxXTicks = Math.floor(xMax / 70) // ~70px per label
    const tickStep = Math.max(1, Math.ceil(animatedData.length / maxXTicks))

    const xTickValues = useMemo(() => {
        return dateScale.domain().filter((_, i) => i % tickStep === 0)
    }, [dateScale, tickStep])

    const crosshairStroke = alpha(theme.palette.text.secondary, 0.3)

    const processHover = useCallback(
        (event: React.MouseEvent<SVGRectElement>) => {
            const { x } = localPoint(event) || { x: 0 }
            let x0 = x - chartMargin.left - leftScaleWidth
            const rightIndex = Math.floor(x0 / (xMax / Math.max(animatedData.length, 1)))
            const leftIndex = rightIndex - 1
            const leftDifference = Math.abs(x0 - (dateScale(animatedData[leftIndex]?.date ?? '') ?? minNum))
            const rightDifference = Math.abs(x0 - (dateScale(animatedData[rightIndex]?.date ?? '') ?? minNum))
            const nearestIndex = leftDifference < rightDifference ? leftIndex : rightIndex
            const nearestDataPoint = animatedData[nearestIndex]
            x0 = (dateScale(nearestDataPoint?.date ?? '') ?? minNum) + Math.round(barWidthRef.current / 2)

            const line = crosshairRef.current
            if (line) {
                if (nearestDataPoint) {
                    line.setAttribute('x1', String(x0))
                    line.setAttribute('x2', String(x0))
                    line.style.visibility = 'visible'
                } else {
                    line.style.visibility = 'hidden'
                }
            }

            const strike = nearestDataPoint?.date ?? null
            if (!nearestDataPoint) {
                lastHoverStrikeRef.current = null
                if (showTooltipRef.current) tooltipRef.current?.hide()
                onHoverRowRef.current?.(null)
                return
            }

            if (strike === lastHoverStrikeRef.current) {
                return
            }

            lastHoverStrikeRef.current = strike
            onHoverRowRef.current?.(nearestDataPoint)
            if (showTooltipRef.current) {
                tooltipRef.current?.show({
                    data: { data: nearestDataPoint },
                    left: x0 + leftScaleWidth + chartMargin.left,
                    top: chartMargin.top,
                })
            }
        },
        [animatedData, chartMargin.left, chartMargin.top, dateScale, leftScaleWidth, xMax]
    )

    const handleMouseMove = useCallback(
        (event: React.MouseEvent<SVGRectElement>) => {
            pendingHoverEventRef.current = event
            if (hoverRafRef.current != null) {
                return
            }
            hoverRafRef.current = requestAnimationFrame(() => {
                hoverRafRef.current = null
                const pending = pendingHoverEventRef.current
                if (pending) {
                    processHover(pending)
                }
            })
        },
        [processHover]
    )

    const handleMouseLeave = useCallback(() => {
        pendingHoverEventRef.current = null
        if (hoverRafRef.current != null) {
            cancelAnimationFrame(hoverRafRef.current)
            hoverRafRef.current = null
        }
        lastHoverStrikeRef.current = null
        if (crosshairRef.current) {
            crosshairRef.current.style.visibility = 'hidden'
        }
        if (showTooltipRef.current) tooltipRef.current?.hide()
        onHoverRowRef.current?.(null)
    }, [])

    useEffect(() => {
        return () => {
            if (hoverRafRef.current != null) {
                cancelAnimationFrame(hoverRafRef.current)
            }
        }
    }, [])

    const yTickFormatter = useCallback((value: number | { valueOf(): number }): string => {
        const frmt = formatLargeNumber(typeof value === 'number' ? value : value.valueOf(), 0)
        return frmt
    }, [])

    const xTickFormatter = useCallback((value: string): string => {
        // const dt = new Date(value)
        // return `${dt.getDate()} ${shortMonthNames[dt.getMonth()]}`
        return value
    }, [])

    // Sidebar previews are ~170px tall; d3's default tick count packs labels on top of each other.
    const yNumTicks = compactBars
        ? Math.max(3, Math.min(5, Math.round(height / 48)))
        : undefined

    if (width <= 0 || height <= 0) {
        return null
    }

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <svg width={width} height={height}>
                <OIChartBarsLayer
                    width={width}
                    height={height}
                    margin={chartMargin}
                    leftScaleWidth={leftScaleWidth}
                    scaleSide={scaleSide}
                    xMax={xMax}
                    yMax={yMax}
                    keys={keys}
                    animatedData={animatedData}
                    data={data}
                    showOI={showOI}
                    metaInfo={metaInfo}
                    dateScale={dateScale}
                    x1Scale={x1Scale}
                    yScale={yScale}
                    yLeftScale={yLeftScale}
                    colorScale={colorScale}
                    xTickValues={xTickValues}
                    yScaleMax={yScaleMax}
                    yScaleMin={yScaleMin}
                    yNumTicks={yNumTicks}
                    barWidthRef={barWidthRef}
                    yTickFormatter={yTickFormatter}
                    xTickFormatter={xTickFormatter}
                />
                <g transform={`translate(${chartMargin.left + leftScaleWidth}, ${chartMargin.top})`} style={{ pointerEvents: 'none' }}>
                    <line
                        ref={crosshairRef}
                        x1={0}
                        y1={0}
                        x2={0}
                        y2={yMax}
                        stroke={crosshairStroke}
                        strokeWidth={1}
                        visibility="hidden"
                    />
                </g>
                <rect
                    x={0}
                    y={0}
                    width={width}
                    height={height}
                    fill="transparent"
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                />
            </svg>
            {showTooltip ? (
                <OIChartTooltipHost ref={tooltipRef} keys={keys} metaInfo={metaInfo} />
            ) : null}
        </Box>
    )
}

const OIChartLegend = memo(({ showOI, metaInfo }: { showOI: boolean; metaInfo: ParticipantChartMetaInfo }) => (
    <Box sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <BarLabelView label="Put OI" color={metaInfo.colors.get('Put OI') ?? participantBarDefaultColor} />
            {showOI && (
                <>
                    <BarLabelView
                        label="Increase"
                        color={metaInfo.colors.get('Put OI') ?? participantBarDefaultColor}
                        type="striped"
                        stripeRotation={45}
                    />
                    <BarLabelView
                        label="Decrease"
                        color={metaInfo.colors.get('Put OI') ?? participantBarDefaultColor}
                        type="hollow"
                    />
                </>
            )}
            <BarLabelView label="Call OI" color={metaInfo.colors.get('Call OI') ?? participantBarDefaultColor} />
            {showOI && (
                <>
                    <BarLabelView
                        label="Increase"
                        color={metaInfo.colors.get('Call OI') ?? participantBarDefaultColor}
                        type="striped"
                        stripeRotation={-45}
                    />
                    <BarLabelView
                        label="Decrease"
                        color={metaInfo.colors.get('Call OI') ?? participantBarDefaultColor}
                        type="hollow"
                    />
                </>
            )}
        </Box>
    </Box>
))

const OIChartCanvasMemo = memo(OIChartCanvas, (prev, next) => {
    if (prev.width !== next.width || prev.height !== next.height) return false
    if (prev.showOI !== next.showOI || prev.replayKey !== next.replayKey) return false
    if (prev.compactBars !== next.compactBars) return false
    if (prev.scaleSide !== next.scaleSide || prev.showTooltip !== next.showTooltip) return false
    return oiChartDataEqual(prev.segmentInfo.data, next.segmentInfo.data)
})

const OIChart = ({
    margin,
    segmentInfo,
    showOI = false,
    replayKey = 0,
    compactBars = false,
    scaleSide = 'left',
    showLegend = true,
    showTooltip = true,
    onHoverRow,
}: ParticipantChartProps) => (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0 }}>
        <Box sx={{ flex: 1, minHeight: 0, width: '100%', height: '100%' }}>
            <ParentSize>
                {({ width, height }) => (
                    <OIChartCanvasMemo
                        margin={margin}
                        segmentInfo={segmentInfo}
                        showOI={showOI}
                        replayKey={replayKey}
                        compactBars={compactBars}
                        scaleSide={scaleSide}
                        showTooltip={showTooltip}
                        onHoverRow={onHoverRow}
                        width={width}
                        height={height}
                    />
                )}
            </ParentSize>
        </Box>
        {showLegend ? (
            <OIChartLegend showOI={showOI} metaInfo={segmentInfo.metaInfo} />
        ) : null}
    </Box>
)

export { OIChart }
export default memo(OIChart, (prev, next) => {
    if (prev.showOI !== next.showOI || prev.replayKey !== next.replayKey) return false
    if (prev.compactBars !== next.compactBars) return false
    if (prev.scaleSide !== next.scaleSide || prev.showLegend !== next.showLegend || prev.showTooltip !== next.showTooltip) return false
    return oiChartDataEqual(prev.segmentInfo.data, next.segmentInfo.data)
})